<script setup>
import { ref, watch } from 'vue'
import { api } from '../../services/api'
import QuestionForm from './QuestionForm.vue'

const props = defineProps({ lesson: { type: Object, required: true }, chapter: { type: Object, required: true }, subject: { type: Object, required: true } })
const emit = defineEmits(['changed', 'select'])
const form = ref({})
const questions = ref([])
const loadingQuestions = ref(false)
const editingQuestion = ref(null)
const saving = ref(false)
const error = ref('')
const typeLabel = { multiple_choice: 'Trắc nghiệm', fill_blank: 'Điền đáp án', essay: 'Tự luận' }
const levelLabel = { basic: 'Cơ bản', application: 'Vận dụng', advanced: 'Nâng cao' }

async function loadQuestions() {
  loadingQuestions.value = true
  try { questions.value = (await api(`/admin/lessons/${props.lesson.id}/questions`)).questions }
  catch (err) { error.value = err.message }
  finally { loadingQuestions.value = false }
}

async function loadLesson() {
  // Danh sách trên cây không có video_url nên lấy chi tiết bài để điền form.
  try {
    const detail = (await api(`/lessons/${props.lesson.id}`)).lesson
    form.value = { title: detail.title, summary: detail.summary || '', duration_minutes: detail.duration_minutes, video_url: detail.video_url || '', is_published: detail.is_published }
  } catch (err) { error.value = err.message }
}

watch(() => props.lesson.id, () => {
  error.value = ''
  editingQuestion.value = null
  form.value = { title: props.lesson.title, summary: props.lesson.summary || '', duration_minutes: props.lesson.duration_minutes, video_url: '', is_published: props.lesson.is_published }
  loadLesson()
  loadQuestions()
}, { immediate: true })

async function saveLesson() {
  if (saving.value) return
  saving.value = true
  error.value = ''
  try {
    await api(`/admin/lessons/${props.lesson.id}`, { method: 'PUT', body: JSON.stringify({ ...form.value, video_url: form.value.video_url.trim() || null }) })
    emit('changed', { message: 'Đã lưu bài học' })
  } catch (err) { error.value = err.message }
  finally { saving.value = false }
}

async function questionSaved(message) {
  editingQuestion.value = null
  await loadQuestions()
  emit('changed', { message })
}

function answerText(question) {
  if (question.type === 'multiple_choice') return `${String.fromCharCode(65 + Number(question.correct_answer))}. ${question.options[Number(question.correct_answer)] ?? ''}`
  return question.correct_answer
}
</script>

<template>
  <div class="panel-body">
    <header class="panel-head">
      <div><p class="eyebrow">Bài học</p><h2>{{ lesson.title }}</h2></div>
      <RouterLink :to="`/bai-hoc/${lesson.id}`" class="btn btn-light btn-sm">Xem như học sinh ↗</RouterLink>
    </header>
    <div v-if="error" class="error-banner" role="alert">{{ error }}</div>

    <form class="panel-section panel-form" @submit.prevent="saveLesson">
      <div class="panel-section-head"><h3>Thông tin bài</h3><span class="chip" :class="form.is_published ? '' : 'purple'">{{ form.is_published ? 'Đang hiển thị' : 'Nháp' }}</span></div>
      <div class="form-grid">
        <div class="form-group wide"><label for="lesson-title">Tên bài học</label><input id="lesson-title" v-model="form.title" class="form-control" required :disabled="saving"></div>
        <div class="form-group wide"><label for="lesson-summary">Mô tả ngắn</label><textarea id="lesson-summary" v-model="form.summary" class="form-control" :disabled="saving"></textarea></div>
        <div class="form-group"><label for="lesson-duration">Thời lượng (phút)</label><input id="lesson-duration" v-model.number="form.duration_minutes" class="form-control" type="number" min="1" max="180" :disabled="saving"></div>
        <div class="form-group"><label for="lesson-video">Link video (không bắt buộc)</label><input id="lesson-video" v-model="form.video_url" class="form-control" type="url" placeholder="https://…" :disabled="saving"></div>
        <label class="check-line wide"><input v-model="form.is_published" type="checkbox" :disabled="saving"><span>Hiển thị cho học sinh<small>Bỏ chọn để ẩn bài; tiến độ và điểm cũ vẫn được giữ.</small></span></label>
      </div>
      <button class="btn btn-primary" :disabled="saving">{{ saving ? 'Đang lưu…' : 'Lưu bài học' }}</button>
      <p class="panel-hint">Nội dung giảng, ví dụ và hình minh họa được biên soạn trong gói học liệu (Nhập / xuất).</p>
    </form>

    <section class="panel-section">
      <div class="panel-section-head"><h3>Câu hỏi luyện tập</h3><span>{{ questions.length }}</span><button v-if="editingQuestion !== 'new'" type="button" class="btn btn-primary btn-sm" @click="editingQuestion = 'new'">＋ Thêm câu hỏi</button></div>
      <QuestionForm v-if="editingQuestion === 'new'" :lesson-id="lesson.id" @saved="questionSaved" @cancel="editingQuestion = null" />
      <p v-if="loadingQuestions && !questions.length" class="panel-hint">Đang tải câu hỏi…</p>
      <p v-else-if="!questions.length" class="panel-hint">Bài chưa có câu hỏi. Thêm câu đầu tiên để học sinh luyện ngay sau bài giảng.</p>
      <article v-for="(question, index) in questions" :key="question.id" class="question-item">
        <div class="question-item-top"><span>Câu {{ index + 1 }} · {{ typeLabel[question.type] }} · {{ levelLabel[question.difficulty] || 'Cơ bản' }} · {{ question.points }} điểm</span><button v-if="editingQuestion?.id !== question.id" type="button" class="text-link" @click="editingQuestion = question">Sửa</button></div>
        <template v-if="editingQuestion?.id !== question.id">
          <p class="question-item-prompt">{{ question.prompt }}</p>
          <p v-if="question.type !== 'essay'" class="question-item-answer">Đáp án: <b>{{ answerText(question) }}</b></p>
        </template>
        <QuestionForm v-else :question="question" :lesson-id="lesson.id" @saved="questionSaved" @cancel="editingQuestion = null" />
      </article>
    </section>
  </div>
</template>

<style scoped>
.question-item { padding: 14px 0; border-top: 1px solid var(--border); }
.question-item-top { display: flex; justify-content: space-between; gap: 10px; color: var(--muted); font-size: 11px; font-weight: 700; }
.question-item-top button { border: 0; background: none; cursor: pointer; }
.question-item-prompt { margin: 8px 0 6px; font-size: 13px; line-height: 1.6; white-space: pre-line; }
.question-item-answer { margin: 0; color: #276448; font-size: 12px; }
</style>
